//  export const getAppPrefix = () => {
import StorageManager from "./storage_manager";
import axios from "axios";
import { hostUrl } from "../../../utils/globals";
//    const hostname = window.location.hostname;
//    const isChannelsDomain =
//      hostname === "channels.social" || hostname.endsWith(".channels.social");
//    return isChannelsDomain ? "" : "/embed/channels";
//  };

export const getAppPrefix = () => {
  const host = localStorage.getItem("embedData");
  if (!host) return "";
  const data = JSON.parse(host);
  const domain = data.domain;
  const isChannelsDomain =
    domain === "channels.social" || domain.endsWith(".channels.social");
  return isChannelsDomain ? "" : "/embed/channels";
};

// localStorage key under which we remember the last channel/topic the user
// opened, scoped per business. Keyed off `embedData` (the widget payload, which
// always carries apiKey + domain) — NOT `embedFetchedData`, whose server
// response has no domain/apiKey, which is why the "reopen where you left off"
// feature silently never saved. Returns null if we can't identify the business.
export const getEmbedLastKey = () => {
  try {
    const ed = JSON.parse(localStorage.getItem("embedData") || "{}");
    const id = ed.apiKey || ed.domain;
    return id ? "embedLast:" + id : null;
  } catch (e) {
    return null;
  }
};

// Public, embed-side fetch of the business's EmbedConfig (branding + features).
export const fetchEmbedConfig = async (apiKey) => {
  if (!apiKey) return null;
  try {
    const { data } = await axios.get(`${hostUrl}/api/embed/public/config`, {
      params: { apiKey },
    });
    return data && data.success ? data : null;
  } catch {
    return null;
  }
};

// Apply the business theme mode (light | dark | auto) by toggling the .dark class.
export const applyEmbedTheme = (themeMode) => {
  if (typeof document === "undefined") return "light";
  let dark = themeMode === "dark";
  if (themeMode === "auto" && typeof window !== "undefined" && window.matchMedia) {
    dark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  // Mark this document as the embed so embed-only theme overrides (scoped to
  // `html.embed-app` in index.css) apply here but never on the marketing site
  // or admin. Embed theming and website theming stay fully separate.
  document.documentElement.classList.add("embed-app");
  document.documentElement.classList.toggle("dark", dark);
  // Remember the resolved mode so the next load can apply it before first paint
  // (avoids a light/white flash while the config request is in flight).
  try {
    localStorage.setItem("embedThemeMode", dark ? "dark" : "light");
  } catch (e) {}
  return dark ? "dark" : "light";
};

// Best-effort synchronous theme for the very first paint, before the async
// config arrives: last resolved mode → widget-passed theme → dark (the embed
// frame is dark, so dark is the safe default that never flashes white).
export const applyInitialEmbedTheme = () => {
  // Pre-fetch paint: default to DARK so we never flash light before the real
  // theme (EmbedConfig.branding.themeMode) arrives. Only trust a PREVIOUSLY
  // RESOLVED theme from a prior load (embedThemeMode) — NOT embedData.theme,
  // which is the widget snippet's param and caused dark communities to flash
  // light on first paint.
  let mode = "dark";
  try {
    const cached = localStorage.getItem("embedThemeMode");
    if (cached === "light" || cached === "dark") mode = cached;
  } catch (e) {}
  return applyEmbedTheme(mode);
};

// #rrggbb (or #rgb) -> "r, g, b" channels, for building rgba() tints.
const hexToRgb = (hex) => {
  if (!hex) return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

// Apply the business's two brand colors:
//  - accentColor (color 1): buttons, active states, own bubble, unread dots,
//    links — plus a derived soft tint for highlight backgrounds.
//  - accentTextColor (color 2): the text/icons that sit ON the accent.
export const applyEmbedAccent = (accentColor, accentTextColor) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (accentColor) {
    root.style.setProperty("--theme-accent", accentColor);
    root.style.setProperty("--theme-accentHover", accentColor);
    root.style.setProperty("--theme-buttonEnable", accentColor);
    const rgb = hexToRgb(accentColor);
    if (rgb) {
      // Soft tint (active-nav / highlight backgrounds) derived from the accent
      // at low alpha, so it reads correctly on both light and dark surfaces.
      root.style.setProperty("--theme-accentSoft", `rgba(${rgb}, 0.14)`);
    }
  }
  if (accentTextColor) {
    root.style.setProperty("--theme-accentText", accentTextColor);
  }
};

// Point the embed document's favicon at the business's icon (favicon, falling
// back to the logo) so a popped-out / standalone embed shows the brand's icon.
export const applyEmbedFavicon = (branding) => {
  if (typeof document === "undefined" || !branding) return;
  const href = branding.favicon || branding.logo;
  if (!href) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
};
